import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Fingerprint, X } from 'lucide-react';
import { toast } from 'sonner';
import { registerPasskey, isPasskeySupported } from '@/lib/passkey';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

const DISMISS_KEY = 'isp_passkey_prompt_dismissed';

export default function PasskeyPrompt() {
  const { token, user } = useAuth();
  const [show, setShow] = useState(false);
  const [name, setName] = useState('');
  const [registering, setRegistering] = useState(false);

  useEffect(() => {
    if (!token || !user || !isPasskeySupported()) return;
    // Don't prompt if previously dismissed
    const dismissed = localStorage.getItem(`${DISMISS_KEY}_${user.id}`);
    if (dismissed) return;

    // Check if user has any passkeys
    api.get('/api/passkeys').then(r => {
      if (r.data.length === 0) {
        // Small delay so it doesn't feel jarring
        setTimeout(() => setShow(true), 2000);
      }
    }).catch(() => {});
  }, [token, user]);

  const handleRegister = async () => {
    setRegistering(true);
    try {
      await registerPasskey(token, name.trim() || 'My Passkey');
      toast.success('Passkey registered! You can now log in with biometrics.');
      setShow(false);
      localStorage.setItem(`${DISMISS_KEY}_${user.id}`, '1');
    } catch (err) {
      toast.error(err.message || 'Failed to register passkey');
    }
    setRegistering(false);
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem(`${DISMISS_KEY}_${user.id}`, '1');
  };

  if (!show) return null;

  return (
    <Dialog open={show} onOpenChange={handleDismiss}>
      <DialogContent data-testid="passkey-prompt-dialog" className="bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 max-w-sm">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
              <Fingerprint size={22} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <DialogTitle className="text-gray-900 dark:text-gray-100 text-base">Enable Passkey Login</DialogTitle>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Log in faster with Face ID, fingerprint, or security key</p>
            </div>
          </div>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Input
            data-testid="passkey-prompt-name"
            placeholder="Passkey name (e.g. iPhone, Laptop)"
            className="h-9 text-sm border-gray-300 dark:border-gray-600"
            value={name}
            onChange={e => setName(e.target.value)}
          />
        </div>
        <DialogFooter className="gap-2">
          <Button data-testid="passkey-prompt-dismiss" variant="ghost" size="sm" className="text-xs text-gray-500" onClick={handleDismiss}>
            Maybe Later
          </Button>
          <Button
            data-testid="passkey-prompt-register"
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
            onClick={handleRegister}
            disabled={registering}
          >
            <Fingerprint size={14} className="mr-1.5" />
            {registering ? 'Setting up...' : 'Set Up Passkey'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
